"use client";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import COItemSearchHelper from "./COItemSearchHelper";
import { useCostCodeMap } from "./useCostCodeMap";

const fmtC = (n) => {
  if (!Number.isFinite(n)) return "$0.00";
  const abs = Math.abs(n);
  const s = abs.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `-$${s}` : `$${s}`;
};
const cls = (...c) => c.filter(Boolean).join(" ");

const SF_LF = ["sf", "lf", "sq ft", "lin ft", "square", "linear", "sheet"];
const isCovUom = (uom) =>
  SF_LF.some((u) => (uom || "").toLowerCase().includes(u));

function calcAutoPrice(item, markupPct) {
  const markup = 1 + (markupPct || 0) / 100;
  const cov =
    item.category === "M" && isCovUom(item.uom)
      ? parseFloat(item.coveragePerUnit) || 1
      : 1;
  const base = item.category === "L" ? item.unitRate || 0 : item.unitCost || 0;
  return Number((base * cov * markup).toFixed(2));
}

function autoMapForBidArea(
  bidArea,
  category,
  purchaseOrders = [],
  workOrders = [],
  budgetItemMap = {},
  itemCode = "",
) {
  if (!bidArea) return { mappedPO3: null, mappedWO3: null };
  const ba = bidArea.toLowerCase().trim();
  if (itemCode) {
    const key = `${itemCode.trim().toLowerCase()}||${ba}`;
    const hit = budgetItemMap[key];
    if (hit) {
      if (category === "M" && hit.mappedPO3)
        return { mappedPO3: hit.mappedPO3, mappedWO3: null };
      if (category === "L" && hit.mappedWO3)
        return { mappedPO3: null, mappedWO3: hit.mappedWO3 };
    }
  }
  if (category === "M") {
    const match = purchaseOrders.find((p) => {
      const title = (p.title || p.bidArea || "").toLowerCase();
      const poNum = String(p.poNumber || "");
      return title.includes(ba) || ba.includes(title) || poNum === ba;
    });
    return { mappedPO3: match?._id?.toString() || null, mappedWO3: null };
  } else {
    const match = workOrders.find((w) => {
      const title = (w.title || w.bidArea || "").toLowerCase();
      const woNum = String(w.woNumber || "");
      return title.includes(ba) || ba.includes(title) || woNum === ba;
    });
    return { mappedPO3: null, mappedWO3: match?._id?.toString() || null };
  }
}

function getMappingOptions(item, purchaseOrders = [], workOrders = []) {
  if (item.category === "L") {
    return {
      label: "Map to WO",
      field: "mappedWO3",
      value: item.mappedWO3?.toString() || "",
      options: (workOrders || []).map((w) => ({
        value: w._id?.toString(),
        label: `WO#${w.woNumber} — ${w.title || w.bidArea || ""} (${(w.type || "").replace(/_/g, " ")})`,
      })),
    };
  }
  return {
    label: "Map to PO",
    field: "mappedPO3",
    value: item.mappedPO3?.toString() || "",
    options: (purchaseOrders || []).map((p) => ({
      value: p._id?.toString(),
      label: `PO#${p.poNumber} — ${p.title || p.bidArea || ""}`,
    })),
  };
}

const fmtQty = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

// ── Bid Area Combobox ──────────────────────────────────────────────────────
function BidAreaCombobox({
  value,
  onChange,
  knownAreas = [],
  placeholder = "Area…",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(value || "");
  const ref = useRef(null);
  const inputRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    setInput(value || "");
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 2,
        left: rect.left,
        minWidth: Math.max(rect.width, 200),
        zIndex: 9999,
      });
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!input.trim()) return knownAreas;
    const q = input.toLowerCase();
    return knownAreas.filter((a) => a.toLowerCase().includes(q));
  }, [input, knownAreas]);

  const select = (area) => {
    setInput(area);
    onChange(area);
    setOpen(false);
  };
  const handleBlur = () => {
    setTimeout(() => {
      onChange(input);
      setOpen(false);
    }, 150);
  };

  const dropdown =
    open && knownAreas.length > 0
      ? createPortal(
          <div
            style={dropdownStyle}
            className="bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-40 overflow-y-auto"
          >
            {filtered.length === 0 && input.trim() ? (
              <div
                className="px-3 py-2 text-xs text-orange-300 hover:bg-gray-700 cursor-pointer"
                onMouseDown={() => select(input.trim())}
              >
                + Add "{input.trim()}"
              </div>
            ) : (
              filtered.map((area) => (
                <div
                  key={area}
                  onMouseDown={() => select(area)}
                  className={cls(
                    "px-3 py-2 text-xs cursor-pointer hover:bg-gray-700",
                    area === value
                      ? "text-orange-300 bg-gray-700/50"
                      : "text-gray-200",
                  )}
                >
                  {area}
                </div>
              ))
            )}
            {input.trim() &&
              filtered.length > 0 &&
              !knownAreas.includes(input.trim()) && (
                <div
                  className="px-3 py-2 text-xs text-orange-300 hover:bg-gray-700 cursor-pointer border-t border-gray-700"
                  onMouseDown={() => select(input.trim())}
                >
                  + Add "{input.trim()}"
                </div>
              )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={ref} className={cls("relative", className)}>
      <input
        ref={inputRef}
        value={input}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setInput(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onBlur={handleBlur}
        className="w-full bg-transparent border-b border-transparent hover:border-gray-500 focus:border-orange-500 focus:outline-none text-gray-300 text-[11px] py-0.5"
      />
      {dropdown}
    </div>
  );
}

// ── Coverage-from-size helper ──────────────────────────────────────────────
function computeCoverageFromSize(size, uom) {
  if (!size || !isCovUom(uom)) return null;
  const s = String(size).trim();
  const parseToFeet = (str) => {
    const t = String(str).trim();
    const parseFrac = (f) => {
      const p = f.split("/");
      return p.length === 2
        ? parseFloat(p[0]) / parseFloat(p[1])
        : parseFloat(f) || 0;
    };
    const m1 = t.match(/^([\d.]+)'\s*([\d]+)[- ]([\d]+\/[\d]+)"?$/);
    if (m1)
      return parseFloat(m1[1]) + (parseFloat(m1[2]) + parseFrac(m1[3])) / 12;
    const m2 = t.match(/^([\d.]+)'([\d.]+)"?$/);
    if (m2) return parseFloat(m2[1]) + (parseFloat(m2[2]) || 0) / 12;
    const m3 = t.match(/^([\d.]+)'?\s+([\d.]+)"?$/);
    if (m3) return parseFloat(m3[1]) + (parseFloat(m3[2]) || 0) / 12;
    if (t.endsWith("'")) return parseFloat(t) || 0;
    if (t.endsWith('"')) return (parseFloat(t) || 0) / 12;
    return parseFloat(t) || 0;
  };
  const ul = (uom || "").toLowerCase();
  if (ul.includes("sf") || ul.includes("sq") || ul.includes("sheet")) {
    const parts = s.replace(/×/g, "x").split(/\s*[xX]\s*/);
    if (parts.length === 2) {
      const w = parseToFeet(parts[0]);
      const h = parseToFeet(parts[1]);
      if (w > 0 && h > 0) return parseFloat((w * h).toFixed(4));
    }
  } else if (ul.includes("lf") || ul.includes("lin")) {
    const len = parseToFeet(s);
    if (len > 0) return parseFloat(len.toFixed(4));
  }
  return null;
}

function CustomDescriptionInput({ value, sectionIdx, ciId, onUpdateCustom }) {
  const [local, setLocal] = useState(value ?? "");
  const isFocused = useRef(false);
  const lastCommitted = useRef(value ?? "");
  useEffect(() => {
    if (isFocused.current) return;
    if (value === lastCommitted.current) return;
    setLocal(value ?? "");
    lastCommitted.current = value ?? "";
  }, [value]);
  return (
    <input
      type="text"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onFocus={() => {
        isFocused.current = true;
      }}
      onBlur={() => {
        isFocused.current = false;
        lastCommitted.current = local;
        onUpdateCustom(sectionIdx, ciId, "description", local);
      }}
      placeholder="Enter description..."
      className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm"
    />
  );
}

// ── Reorder button pair — used in both BD and custom rows ─────────────────
function ReorderButtons({ isFirst, isLast, onUp, onDown }) {
  return (
    <div className="flex flex-col gap-0.5">
      <button
        disabled={isFirst}
        onClick={onUp}
        className={cls(
          "w-4 h-3.5 flex items-center justify-center rounded text-[9px] leading-none transition-colors",
          isFirst
            ? "text-gray-700 cursor-not-allowed"
            : "text-gray-500 hover:text-orange-300 hover:bg-gray-700",
        )}
        title="Move up"
      >
        ▲
      </button>
      <button
        disabled={isLast}
        onClick={onDown}
        className={cls(
          "w-4 h-3.5 flex items-center justify-center rounded text-[9px] leading-none transition-colors",
          isLast
            ? "text-gray-700 cursor-not-allowed"
            : "text-gray-500 hover:text-orange-300 hover:bg-gray-700",
        )}
        title="Move down"
      >
        ▼
      </button>
    </div>
  );
}

// ── Custom Item Row ────────────────────────────────────────────────────────
function CustomItemRow({
  item,
  sectionIdx,
  isReadOnly = false,
  onUpdateCustom,
  onRemoveCustom,
  contractLabourRates = [],
  reorderSlot = null,
}) {
  const [desc, setDesc] = useState(item.description || "");
  const [qty, setQty] = useState(item.qty ?? 1);
  const [unit, setUnit] = useState(item.unit || "EA");
  const [price, setPrice] = useState(item.unitPrice ?? 0);
  const [showRatePicker, setShowRatePicker] = useState(false);
  const rateButtonRef = useRef(null);
  const [rateDropStyle, setRateDropStyle] = useState({});

  const total = Number(
    ((parseFloat(qty) || 0) * (parseFloat(price) || 0)).toFixed(2),
  );
  const isLabour = item.category === "L";
  const hasRates = isLabour && contractLabourRates.length > 0;

  useEffect(() => {
    if (!showRatePicker || !rateButtonRef.current) return;

    const updatePos = () => {
      if (!rateButtonRef.current) return;
      const rect = rateButtonRef.current.getBoundingClientRect();
      const dropdownHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom;
      const goAbove = spaceBelow < dropdownHeight && rect.top > spaceBelow;
      setRateDropStyle({
        position: "fixed",
        ...(goAbove
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
        left: rect.left,
        width: 240,
        zIndex: 9999,
      });
    };

    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [showRatePicker]);

  useEffect(() => {
    if (!showRatePicker) return;
    const handler = (e) => {
      if (!rateButtonRef.current?.contains(e.target)) setShowRatePicker(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showRatePicker]);

  const commit = (field, value) =>
    onUpdateCustom(sectionIdx, item._id, field, value);

  const BadgeCell = () => (
    <td className="py-2 px-2">
      <div className="flex items-center gap-1">
        {reorderSlot}
        <span className="w-5 h-5 rounded flex items-center justify-center bg-yellow-500/20">
          <span className="text-[10px] font-bold text-yellow-400">C</span>
        </span>
      </div>
    </td>
  );

  if (isReadOnly) {
    return (
      <tr className="border-t border-gray-700">
        <BadgeCell />
        <td className="py-2 px-2 text-sm text-gray-300">
          {item.description || "—"}
        </td>
        <td className="py-2 px-2 text-right text-sm text-gray-300">
          {item.qty ?? 1}
        </td>
        <td className="py-2 px-2 text-center text-sm text-gray-300">
          {item.unit || "EA"}
        </td>
        <td className="py-2 px-2 text-right text-sm text-gray-300">
          {fmtC(parseFloat(item.unitPrice) || 0)}
        </td>
        <td className="py-2 px-2 text-right text-gray-300 font-medium text-sm">
          {fmtC(total)}
        </td>
        <td className="py-2 px-2" />
      </tr>
    );
  }

  return (
    <tr className="border-t border-gray-700">
      <BadgeCell />
      <td className="py-2 px-2">
        <input
          type="text"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          onBlur={() => commit("description", desc)}
          placeholder="Enter description..."
          className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm"
        />
      </td>
      <td className="py-2 px-2">
        <input
          type="text"
          inputMode="decimal"
          value={qty === 0 ? "" : qty}
          onChange={(e) => setQty(e.target.value)}
          onBlur={() => {
            const v = parseFloat(parseFloat(qty).toFixed(2));
            const final = isNaN(v) ? 1 : v;
            setQty(final);
            commit("qty", final);
          }}
          placeholder="1"
          className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm text-right"
        />
      </td>
      <td className="py-2 px-2">
        <input
          type="text"
          value={unit}
          onChange={(e) => setUnit(e.target.value.toUpperCase())}
          onBlur={() => commit("unit", unit)}
          className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm text-center"
        />
      </td>
      <td className="py-2 px-2">
        <div className="flex items-center gap-1 w-full">
          <input
            type="text"
            inputMode="decimal"
            value={price === 0 ? "" : price}
            onChange={(e) => setPrice(e.target.value)}
            onBlur={() => {
              const v = parseFloat(parseFloat(price).toFixed(2));
              const final = isNaN(v) ? 0 : v;
              setPrice(final);
              commit("unitPrice", final);
            }}
            placeholder="0.00"
            className="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm text-right"
          />
          {hasRates && (
            <>
              <button
                ref={rateButtonRef}
                onClick={() => setShowRatePicker((p) => !p)}
                title="Select contract labour rate"
                className={cls(
                  "px-2 py-1 rounded text-[10px] border transition-colors whitespace-nowrap",
                  showRatePicker
                    ? "bg-purple-600/60 border-purple-500 text-purple-200"
                    : "bg-purple-700/40 border-purple-600/40 text-purple-300 hover:bg-purple-700/70",
                )}
              >
                Labour Rates
              </button>
              {showRatePicker &&
                createPortal(
                  <div
                    style={rateDropStyle}
                    className="bg-gray-800 border border-purple-600/50 rounded-lg shadow-2xl overflow-hidden"
                  >
                    <div className="px-3 py-2 border-b border-gray-700 bg-gray-900/60">
                      <p className="text-[10px] font-semibold text-purple-300 uppercase tracking-wider">
                        Contract Labour Rates
                      </p>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {contractLabourRates.map((r) => (
                        <button
                          key={r._id}
                          onMouseDown={() => {
                            const rate = parseFloat(r.unitRate) || 0;
                            const title = r.labourItem?.title || r.title || "";
                            const uom = (r.uom || "EA").toUpperCase();
                            setPrice(rate);
                            if (title) setDesc(title);
                            setUnit(uom);
                            commit("unitPrice", rate);
                            if (title) commit("description", title);
                            commit("unit", uom);
                            setShowRatePicker(false);
                          }}
                          className="w-full text-left px-3 py-2.5 text-xs text-gray-200 hover:bg-purple-900/30 flex justify-between items-center gap-3 border-b border-gray-700/50 last:border-0"
                        >
                          <span className="truncate text-gray-200">
                            {r.labourItem?.title || r.title || "Rate"}
                          </span>
                          <span className="text-purple-300 font-mono shrink-0 font-semibold">
                            ${parseFloat(r.unitRate).toFixed(2)}/{r.uom || "EA"}
                          </span>
                        </button>
                      ))}
                    </div>
                    <div className="px-3 py-1.5 border-t border-gray-700 bg-gray-900/40">
                      <button
                        onMouseDown={() => setShowRatePicker(false)}
                        className="w-full text-center text-[10px] text-gray-500 hover:text-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>,
                  document.body,
                )}
            </>
          )}
        </div>
      </td>
      <td className="py-2 px-2 text-right text-gray-300 font-medium text-sm">
        {fmtC(total)}
      </td>
      <td className="py-2 px-2 text-center">
        <button
          onClick={() => onRemoveCustom(sectionIdx, item._id)}
          className="text-red-400 hover:text-red-300 p-1"
        >
          ×
        </button>
      </td>
    </tr>
  );
}

// ── Breakdown Row ──────────────────────────────────────────────────────────
function BreakdownRow({
  item,
  sectionIdx,
  isCustom,
  isReadOnly,
  markupPct,
  onUpdateItem,
  onUpdateCustom,
  onRemoveCustom,
  contractLabourRates = [],
  reorderSlot = null,
}) {
  const [showRatePicker, setShowRatePicker] = useState(false);
  const editable = !isReadOnly;
  const rateButtonRef = useRef(null);
  const [rateDropdownStyle, setRateDropdownStyle] = useState({});

  const autoPrice = isCustom ? 0 : calcAutoPrice(item, markupPct);
  const price = isCustom
    ? parseFloat(item.unitPrice) || 0
    : item.breakdownUnitPrice !== undefined && item.breakdownUnitPrice !== null
      ? parseFloat(item.breakdownUnitPrice) || 0
      : autoPrice;
  const qty = isCustom
    ? parseFloat(item.qty) || 1
    : (item.breakdownQty ?? Math.abs(item.deltaQty || 0));
  const total = Number(
    ((parseFloat(qty) || 0) * (parseFloat(price) || 0)).toFixed(2),
  );

  const isLabour = item.category === "L";
  const hasRates = isLabour && contractLabourRates.length > 0;

  useEffect(() => {
    if (!showRatePicker || !rateButtonRef.current) return;

    const updatePos = () => {
      if (!rateButtonRef.current) return;
      const rect = rateButtonRef.current.getBoundingClientRect();
      const dropdownHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom;
      const goAbove = spaceBelow < dropdownHeight && rect.top > spaceBelow;
      setRateDropdownStyle({
        position: "fixed",
        ...(goAbove
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
        left: rect.left,
        width: 240,
        zIndex: 9999,
      });
    };

    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [showRatePicker]);

  useEffect(() => {
    if (!showRatePicker) return;
    const handler = (e) => {
      if (!rateButtonRef.current?.contains(e.target)) setShowRatePicker(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showRatePicker]);

  const contractMatch =
    !isCustom && isLabour && contractLabourRates.length > 0
      ? contractLabourRates.find((r) => {
          const rateCode = (
            r.itemCode ||
            r.labourItem?.itemCode ||
            r.labourItem?.code ||
            ""
          )
            .trim()
            .toLowerCase();
          const rateTitle = (r.labourItem?.title || r.title || "")
            .trim()
            .toLowerCase();
          const itemCode = (item.itemCode || "").trim().toLowerCase();
          const itemDesc = (
            item.customerDescription ||
            item.costCodeDescription ||
            ""
          )
            .trim()
            .toLowerCase();
          if (rateCode && itemCode && rateCode === itemCode) return true;
          if (
            rateTitle &&
            itemDesc &&
            (itemDesc.includes(rateTitle) || rateTitle.includes(itemDesc))
          )
            return true;
          return false;
        })
      : null;

  if (isCustom) return null;

  return (
    <tr className="border-t border-gray-700">
      <td className="py-2 px-2">
        <div className="flex items-center gap-1">
          {reorderSlot}
          <span className="w-5 h-5 rounded flex items-center justify-center bg-blue-500/20">
            <span className="text-[10px] font-bold text-blue-400">B</span>
          </span>
        </div>
      </td>

      <td className="py-2 px-2">
        {editable ? (
          <CustomDescriptionInput
            value={
              item.breakdownDescription ||
              item.customerDescription ||
              item.costCodeDescription ||
              ""
            }
            sectionIdx={sectionIdx}
            ciId={item._localId}
            onUpdateCustom={(_sIdx, _ciId, _field, val) =>
              onUpdateItem(item._localId, "breakdownDescription", val)
            }
          />
        ) : (
          <span className="text-sm text-gray-300">
            {item.breakdownDescription ||
              item.customerDescription ||
              item.costCodeDescription ||
              "—"}
          </span>
        )}
      </td>

      <td className="py-2 px-2">
        {editable ? (
          <input
            type="text"
            inputMode="decimal"
            value={item.breakdownQty ?? Math.abs(item.deltaQty || 0)}
            onChange={(e) =>
              onUpdateItem(
                item._localId,
                "breakdownQty",
                parseFloat(e.target.value) || 0,
              )
            }
            placeholder="1"
            className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm text-right"
          />
        ) : (
          <span className="text-sm text-gray-300 block text-right">
            {Number(qty).toFixed(2)}
          </span>
        )}
      </td>

      <td className="py-2 px-2">
        {editable ? (
          <input
            type="text"
            value={item.breakdownUnit || item.uom || "EA"}
            onChange={(e) =>
              onUpdateItem(
                item._localId,
                "breakdownUnit",
                e.target.value.toUpperCase(),
              )
            }
            className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm text-center"
          />
        ) : (
          <span className="text-sm text-gray-300 block text-center">
            {(item.breakdownUnit || item.uom || "EA").toUpperCase()}
          </span>
        )}
      </td>

      <td className="py-2 px-2">
        {editable ? (
          <div className="flex flex-col items-end gap-0.5">
            <div className="flex items-center gap-1 w-full">
              <input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={price}
                onChange={(e) =>
                  onUpdateItem(
                    item._localId,
                    "breakdownUnitPrice",
                    parseFloat(e.target.value) || 0,
                  )
                }
                placeholder="0.00"
                className="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm text-right
            [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none
            focus:outline-none focus:border-orange-500"
              />

              {hasRates && (
                <>
                  <button
                    ref={rateButtonRef}
                    onClick={() => setShowRatePicker((p) => !p)}
                    title="Select contract labour rate"
                    className={cls(
                      "px-2 py-1 rounded text-[10px] border transition-colors whitespace-nowrap",
                      showRatePicker
                        ? "bg-purple-600/60 border-purple-500 text-purple-200"
                        : "bg-purple-700/40 border-purple-600/40 text-purple-300 hover:bg-purple-700/70",
                    )}
                  >
                    Labour Rates
                  </button>
                  {showRatePicker &&
                    createPortal(
                      <div
                        style={rateDropdownStyle}
                        className="bg-gray-800 border border-purple-600/50 rounded-lg shadow-2xl overflow-hidden"
                      >
                        <div className="px-3 py-2 border-b border-gray-700 bg-gray-900/60">
                          <p className="text-[10px] font-semibold text-purple-300 uppercase tracking-wider">
                            Contract Labour Rates
                          </p>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {contractLabourRates.map((r) => (
                            <button
                              key={r._id}
                              onMouseDown={() => {
                                const rate = parseFloat(r.unitRate) || 0;
                                const title =
                                  r.labourItem?.title || r.title || "";
                                const uom = r.uom || "EA";
                                onUpdateItem(
                                  item._localId,
                                  "breakdownUnitPrice",
                                  rate,
                                );
                                if (title)
                                  onUpdateItem(
                                    item._localId,
                                    "breakdownDescription",
                                    title,
                                  );
                                onUpdateItem(
                                  item._localId,
                                  "breakdownUnit",
                                  uom.toUpperCase(),
                                );
                                setShowRatePicker(false);
                              }}
                              className="w-full text-left px-3 py-2.5 text-xs text-gray-200 hover:bg-purple-900/30 flex justify-between items-center gap-3 border-b border-gray-700/50 last:border-0"
                            >
                              <span className="truncate text-gray-200">
                                {r.labourItem?.title || r.title || "Rate"}
                              </span>
                              <span className="text-purple-300 font-mono shrink-0 font-semibold">
                                ${parseFloat(r.unitRate).toFixed(2)}/
                                {r.uom || "EA"}
                              </span>
                            </button>
                          ))}
                        </div>
                        <div className="px-3 py-1.5 border-t border-gray-700 bg-gray-900/40">
                          <button
                            onMouseDown={() => setShowRatePicker(false)}
                            className="w-full text-center text-[10px] text-gray-500 hover:text-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>,
                      document.body,
                    )}
                </>
              )}
            </div>
          </div>
        ) : (
          <span className="text-sm text-gray-300 block text-right">
            {fmtC(price)}
          </span>
        )}
      </td>

      <td className="py-2 px-2 text-right text-gray-300 font-medium text-sm">
        {fmtC(total)}
      </td>
      <td className="py-2 px-2 text-center" />
    </tr>
  );
}

// ── Breakdown Table ────────────────────────────────────────────────────────
function BreakdownTable({
  category,
  bdItems,
  customItems,
  total,
  sectionIdx,
  isReadOnly,
  markupPct,
  onUpdateItem,
  onUpdateCustomItem,
  onRemoveCustomItem,
  onAddCustomItem,
  onMoveItem,
  onMoveCustom,
  contractLabourRates = [],
}) {
  const isM = category === "M";
  const color = isM ? "blue" : "orange";
  const catCustom = (customItems || []).filter((c) => c.category === category);
  const totalRows = (bdItems || []).length + catCustom.length;

  return (
    <div className={isM ? "mb-6" : ""}>
      <div className="flex items-center justify-between mb-2">
        <h5 className={`text-xs font-semibold text-${color}-400`}>
          {isM ? "Material" : "Labour"}
          {!isM && contractLabourRates.length > 0 && (
            <span className="ml-2 text-[10px] text-purple-400 font-normal">
              ★ contract rates available
            </span>
          )}
        </h5>
        {!isReadOnly && (
          <button
            onClick={() => onAddCustomItem(sectionIdx, category)}
            className={`px-2 py-1 text-[11px] text-${color}-400 bg-${color}-500/10 border border-${color}-500/30 rounded hover:bg-${color}-500/20 flex items-center gap-1`}
          >
            + Custom Row
          </button>
        )}
      </div>

      {totalRows > 0 ? (
        <div className="rounded-lg border border-gray-700/50 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500">
                <th className="text-left py-1 px-2 w-8"></th>
                <th className="text-left py-1 px-2">Description</th>
                <th className="text-right py-1 px-2 w-32">Qty</th>
                <th className="text-center py-1 px-2 w-20">Unit</th>
                <th className="text-right py-1 px-2 w-36">Unit Price</th>
                <th className="text-right py-1 px-2 w-28">Total</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {(bdItems || []).map((item, bdIdx) => {
                const rowPos = bdIdx;
                const isFirstRow = rowPos === 0;
                const isLastRow = rowPos === totalRows - 1;
                const reorderSlot = !isReadOnly ? (
                  <ReorderButtons
                    isFirst={isFirstRow}
                    isLast={isLastRow}
                    onUp={() =>
                      onMoveItem && onMoveItem(sectionIdx, item._localId, -1)
                    }
                    onDown={() =>
                      onMoveItem && onMoveItem(sectionIdx, item._localId, 1)
                    }
                  />
                ) : null;
                return (
                  <BreakdownRow
                    key={`bd-${item._localId || item._id || bdIdx}-${bdIdx}`}
                    item={item}
                    sectionIdx={sectionIdx}
                    isCustom={false}
                    isReadOnly={isReadOnly}
                    markupPct={markupPct}
                    onUpdateItem={onUpdateItem}
                    onUpdateCustom={onUpdateCustomItem}
                    onRemoveCustom={onRemoveCustomItem}
                    contractLabourRates={contractLabourRates}
                    reorderSlot={reorderSlot}
                  />
                );
              })}
              {catCustom.map((ci, ciIdx) => {
                const rowPos = (bdItems || []).length + ciIdx;
                const isFirstRow = rowPos === 0;
                const isLastRow = rowPos === totalRows - 1;
                const reorderSlot = !isReadOnly ? (
                  <ReorderButtons
                    isFirst={isFirstRow}
                    isLast={isLastRow}
                    onUp={() =>
                      onMoveCustom && onMoveCustom(sectionIdx, ci._id, -1)
                    }
                    onDown={() =>
                      onMoveCustom && onMoveCustom(sectionIdx, ci._id, 1)
                    }
                  />
                ) : null;
                return (
                  <CustomItemRow
                    key={ci._id}
                    item={ci}
                    sectionIdx={sectionIdx}
                    isReadOnly={isReadOnly}
                    onUpdateCustom={onUpdateCustomItem}
                    onRemoveCustom={onRemoveCustomItem}
                    contractLabourRates={contractLabourRates}
                    reorderSlot={reorderSlot}
                  />
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-600">
                <td
                  colSpan={5}
                  className="py-2 px-2 text-right text-gray-400 font-medium text-xs"
                >
                  {isM ? "Material" : "Labour"} Total:
                </td>
                <td
                  className={`py-2 px-2 text-right text-${color}-400 font-bold text-xs`}
                >
                  {fmtC(total || 0)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="text-center py-3 text-gray-600 text-xs border border-dashed border-gray-700/50 rounded">
          No {isM ? "material" : "labour"} rows — toggle items in Cost Items tab
          or add a Custom Row
        </div>
      )}
    </div>
  );
}

// ── Mass Edit Panel ────────────────────────────────────────────────────────
function MassEditPanel({
  items,
  sections,
  purchaseOrders: initialPOs,
  workOrders: initialWOs,
  budgetItemMap = {},
  knownBidAreas,
  onUpdateItem,
  onClose,
  projectId,
  unionOptions = [{ value: "non-union", label: "Non-Union" }],
  token,
  resolveName = (n) => n,
}) {
  const API = "https://erp-production-72da01c8e651.herokuapp.com/api";
  const headers = { Authorization: `Bearer ${token}` };
  const [purchaseOrders, setPurchaseOrders] = useState(initialPOs || []);
  const [workOrders, setWorkOrders] = useState(initialWOs || []);
  const [tab, setTab] = useState("M");
  const [bidAreaFilter, setBidAreaFilter] = useState("all");
  const [selectedLocalIds, setSelectedLocalIds] = useState([]);
  const [massArea, setMassArea] = useState("");
  const [massPO, setMassPO] = useState("");
  const [massWO, setMassWO] = useState("");
  const [showCreatePO, setShowCreatePO] = useState(false);
  const [newPOTitle, setNewPOTitle] = useState("");
  const [newPOBidArea, setNewPOBidArea] = useState("");
  const [creatingPO, setCreatingPO] = useState(false);
  const [showCreateWO, setShowCreateWO] = useState(false);
  const [newWOTitle, setNewWOTitle] = useState("");
  const [newWOType, setNewWOType] = useState("piecework");
  const [newWOUnion, setNewWOUnion] = useState("non-union");
  const [creatingWO, setCreatingWO] = useState(false);
  const [createError, setCreateError] = useState("");
  const [sectionFilter, setSectionFilter] = useState("all");

  const eligibleItems = useMemo(
    () =>
      items.filter(
        (i) =>
          i.referenceNumber != null &&
          i.referenceNumber !== undefined &&
          i.category === tab,
      ),
    [items, tab],
  );
  const tabBidAreas = useMemo(
    () =>
      [
        "all",
        ...new Set(
          eligibleItems.map((i) => i.bidArea).filter(Boolean),
        ).values(),
      ].sort(),
    [eligibleItems],
  );
  const tabSections = useMemo(
    () => [
      "all",
      ...[
        ...new Set(
          eligibleItems.map((i) => i.sectionNumber).filter((n) => n != null),
        ),
      ].sort((a, b) => String(a).localeCompare(String(b))),
    ],
    [eligibleItems],
  );
  const visibleItems = useMemo(
    () =>
      eligibleItems.filter((i) => {
        const matchBid = bidAreaFilter === "all" || i.bidArea === bidAreaFilter;
        const matchSec =
          sectionFilter === "all" || i.sectionNumber === sectionFilter;
        return matchBid && matchSec;
      }),
    [eligibleItems, bidAreaFilter, sectionFilter],
  );

  useEffect(() => {
    setSelectedLocalIds([]);
  }, [tab, bidAreaFilter, sectionFilter]);

  const toggleAll = () => {
    if (selectedLocalIds.length === visibleItems.length)
      setSelectedLocalIds([]);
    else setSelectedLocalIds(visibleItems.map((i) => i._localId));
  };
  const toggle = (id) =>
    setSelectedLocalIds((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id],
    );
  const sectionLabel = (sn) => {
    if (!sn) return "—";
    const name = resolveName(sn);
    return name && name !== sn ? `§${sn} ${name}` : `§${sn}`;
  };

  const applyMassEdit = () => {
    if (!selectedLocalIds.length) return;
    selectedLocalIds.forEach((localId) => {
      const item = items.find((i) => i._localId === localId);
      if (!item) return;
      if (massArea) {
        onUpdateItem(localId, "bidArea", massArea);
        const mapped = autoMapForBidArea(
          massArea,
          item.category,
          purchaseOrders,
          workOrders,
          budgetItemMap,
          item.itemCode,
        );
        if (item.category === "M" && mapped.mappedPO3 && !massPO)
          onUpdateItem(localId, "mappedPO3", mapped.mappedPO3, "mappedWO3");
        else if (item.category === "L" && mapped.mappedWO3 && !massWO)
          onUpdateItem(localId, "mappedWO3", mapped.mappedWO3, "mappedPO3");
      }
      if (massPO && item.category === "M")
        onUpdateItem(localId, "mappedPO3", massPO, "mappedWO3");
      if (massWO && item.category === "L")
        onUpdateItem(localId, "mappedWO3", massWO, "mappedPO3");
    });
    setSelectedLocalIds([]);
    setMassArea("");
    setMassPO("");
    setMassWO("");
  };

  const handleCreatePO = async () => {
    if (!newPOTitle.trim()) return;
    setCreatingPO(true);
    setCreateError("");
    try {
      const res = await axios.post(
        `${API}/purchaseOrders3/project/${projectId}`,
        { title: newPOTitle.trim(), bidArea: newPOBidArea.trim() },
        { headers },
      );
      const created = res.data.purchaseOrder;
      setPurchaseOrders((p) => [...p, created]);
      setMassPO(created._id.toString());
      setNewPOTitle("");
      setNewPOBidArea("");
      setShowCreatePO(false);
    } catch (err) {
      setCreateError(err.response?.data?.message || err.message);
    } finally {
      setCreatingPO(false);
    }
  };

  const handleCreateWO = async () => {
    if (!newWOTitle.trim()) return;
    setCreatingWO(true);
    setCreateError("");
    try {
      const union = newWOType === "subcontractor" ? "non-union" : newWOUnion;
      const res = await axios.post(
        `${API}/workorders3/project/${projectId}`,
        { title: newWOTitle.trim(), type: newWOType, union },
        { headers },
      );
      const created = res.data.workOrder;
      setWorkOrders((w) => [...w, created]);
      setMassWO(created._id.toString());
      setNewWOTitle("");
      setNewWOType("piecework");
      setNewWOUnion("non-union");
      setShowCreateWO(false);
    } catch (err) {
      setCreateError(err.response?.data?.message || err.message);
    } finally {
      setCreatingWO(false);
    }
  };

  const canApply =
    selectedLocalIds.length > 0 && (massArea || massPO || massWO);

  return (
    <div className="bg-gray-800 border border-orange-500/30 rounded-xl p-4 mb-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-orange-400">
          Mass Edit — Bid Area &amp; Mapping
        </h4>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-300 text-lg leading-none"
        >
          ×
        </button>
      </div>
      <div className="flex rounded-lg overflow-hidden border border-gray-700 w-fit">
        {[
          { v: "M", l: "Material (PO)" },
          { v: "L", l: "Labour (WO)" },
        ].map(({ v, l }) => (
          <button
            key={v}
            onClick={() => setTab(v)}
            className={cls(
              "px-4 py-1.5 text-xs font-medium transition-colors",
              tab === v
                ? v === "M"
                  ? "bg-blue-600 text-white"
                  : "bg-orange-500 text-white"
                : "bg-gray-800 text-gray-400 hover:text-gray-200",
            )}
          >
            {l}
          </button>
        ))}
      </div>
      {(tabBidAreas.length > 1 || tabSections.length > 2) && (
        <div className="space-y-2">
          {tabBidAreas.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-gray-500 uppercase tracking-wide w-20 shrink-0">
                Bid Area:
              </span>
              <div className="flex flex-wrap gap-1">
                {tabBidAreas.map((a) => (
                  <button
                    key={a}
                    onClick={() => setBidAreaFilter(a)}
                    className={cls(
                      "px-2.5 py-0.5 text-[11px] rounded-full border transition-colors",
                      bidAreaFilter === a
                        ? "bg-orange-500 border-orange-500 text-white"
                        : "bg-gray-700 border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-200",
                    )}
                  >
                    {a === "all" ? "All" : a}
                  </button>
                ))}
              </div>
            </div>
          )}
          {tabSections.length > 2 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-gray-500 uppercase tracking-wide w-20 shrink-0">
                Section:
              </span>
              <div className="flex flex-wrap gap-1">
                {tabSections.map((n) => (
                  <button
                    key={n}
                    onClick={() => setSectionFilter(n)}
                    className={cls(
                      "px-2.5 py-0.5 text-[11px] rounded-full border transition-colors",
                      sectionFilter === n
                        ? "bg-indigo-500 border-indigo-500 text-white"
                        : "bg-gray-700 border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-200",
                    )}
                  >
                    {n === "all" ? "All" : sectionLabel(n)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">
            Set Bid Area (auto-maps {tab === "M" ? "PO" : "WO"})
          </p>
          <select
            value={massArea}
            onChange={(e) => setMassArea(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-gray-200 text-xs focus:border-orange-500 focus:outline-none"
          >
            <option value="">— Keep current —</option>
            {knownBidAreas.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
        {tab === "M" ? (
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Assign to PO
              </p>
              <button
                onClick={() => {
                  setShowCreatePO((p) => !p);
                  setShowCreateWO(false);
                  setCreateError("");
                }}
                className="text-[10px] text-orange-400 hover:text-orange-300"
              >
                {showCreatePO ? "✕ Cancel" : "+ New PO"}
              </button>
            </div>
            <select
              value={massPO}
              onChange={(e) => setMassPO(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-gray-200 text-xs focus:border-orange-500 focus:outline-none"
            >
              <option value="">— Auto / keep current —</option>
              {purchaseOrders.map((p) => (
                <option key={p._id} value={p._id?.toString()}>
                  PO#{p.poNumber} — {p.title || p.bidArea || ""}
                </option>
              ))}
            </select>
            {showCreatePO && (
              <div className="mt-2 p-3 bg-gray-900/60 border border-gray-700 rounded-lg space-y-2">
                <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide">
                  New Purchase Order
                </p>
                <input
                  autoFocus
                  type="text"
                  value={newPOTitle}
                  onChange={(e) => setNewPOTitle(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreatePO()}
                  placeholder="PO title *"
                  className="w-full px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                />
                <input
                  type="text"
                  value={newPOBidArea}
                  onChange={(e) => setNewPOBidArea(e.target.value)}
                  placeholder="Bid area / floor (optional)"
                  className="w-full px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                />
                <button
                  onClick={handleCreatePO}
                  disabled={!newPOTitle.trim() || creatingPO}
                  className="w-full py-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {creatingPO && (
                    <span className="animate-spin inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full" />
                  )}
                  {creatingPO ? "Creating…" : "Create & Select"}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Assign to WO
              </p>
              <button
                onClick={() => {
                  setShowCreateWO((p) => !p);
                  setShowCreatePO(false);
                  setCreateError("");
                }}
                className="text-[10px] text-orange-400 hover:text-orange-300"
              >
                {showCreateWO ? "✕ Cancel" : "+ New WO"}
              </button>
            </div>
            <select
              value={massWO}
              onChange={(e) => setMassWO(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-gray-200 text-xs focus:border-orange-500 focus:outline-none"
            >
              <option value="">— Auto / keep current —</option>
              {workOrders.map((w) => (
                <option key={w._id} value={w._id?.toString()}>
                  WO#{w.woNumber} — {w.title || ""} (
                  {(w.type || "").replace(/_/g, " ")})
                </option>
              ))}
            </select>
            {showCreateWO && (
              <div className="mt-2 p-3 bg-gray-900/60 border border-gray-700 rounded-lg space-y-2">
                <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide">
                  New Work Order
                </p>
                <input
                  autoFocus
                  type="text"
                  value={newWOTitle}
                  onChange={(e) => setNewWOTitle(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreateWO()}
                  placeholder="WO title *"
                  className="w-full px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                />
                <div className="flex gap-2">
                  <select
                    value={newWOType}
                    onChange={(e) => {
                      setNewWOType(e.target.value);
                      if (e.target.value === "subcontractor")
                        setNewWOUnion("non-union");
                    }}
                    className="flex-1 px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none"
                  >
                    <option value="piecework">Piecework</option>
                    <option value="hourly">Hourly</option>
                    <option value="subcontractor">Subcontractor</option>
                  </select>
                  <select
                    value={newWOUnion}
                    disabled={newWOType === "subcontractor"}
                    onChange={(e) => setNewWOUnion(e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none disabled:opacity-40"
                  >
                    {unionOptions.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleCreateWO}
                  disabled={!newWOTitle.trim() || creatingWO}
                  className="w-full py-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {creatingWO && (
                    <span className="animate-spin inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full" />
                  )}
                  {creatingWO ? "Creating…" : "Create & Select"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {createError && (
        <div className="flex items-center justify-between px-3 py-2 bg-red-900/40 border border-red-700/60 rounded text-xs text-red-300">
          <span>{createError}</span>
          <button
            onClick={() => setCreateError("")}
            className="ml-2 text-red-500 hover:text-red-300"
          >
            ✕
          </button>
        </div>
      )}
      <div className="border border-gray-700 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-900/60 sticky top-0">
            <tr>
              <th className="px-2 py-1.5 w-8">
                <input
                  type="checkbox"
                  checked={
                    visibleItems.length > 0 &&
                    selectedLocalIds.length === visibleItems.length
                  }
                  onChange={toggleAll}
                  className="accent-orange-500"
                />
              </th>
              <th className="px-2 py-1.5 text-left text-gray-500">Code</th>
              <th className="px-2 py-1.5 text-left text-gray-500">
                Description
              </th>
              <th className="px-2 py-1.5 text-left text-gray-500">Section</th>
              <th className="px-2 py-1.5 text-left text-gray-500">Bid Area</th>
              <th className="px-2 py-1.5 text-left text-gray-500">
                {tab === "M" ? "Mapped PO" : "Mapped WO"}
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-3 py-6 text-center text-gray-600 italic"
                >
                  No {tab === "M" ? "material" : "labour"} items
                  {bidAreaFilter !== "all"
                    ? ` in bid area "${bidAreaFilter}"`
                    : ""}
                </td>
              </tr>
            ) : (
              visibleItems.map((item) => {
                const isSel = selectedLocalIds.includes(item._localId);
                const mapped =
                  tab === "M"
                    ? purchaseOrders.find(
                        (p) => p._id?.toString() === item.mappedPO3?.toString(),
                      )
                    : workOrders.find(
                        (w) => w._id?.toString() === item.mappedWO3?.toString(),
                      );
                return (
                  <tr
                    key={item._localId}
                    onClick={() => toggle(item._localId)}
                    className={cls(
                      "border-t border-gray-700/50 cursor-pointer transition-colors",
                      isSel ? "bg-orange-900/20" : "hover:bg-gray-700/30",
                    )}
                  >
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggle(item._localId)}
                        className="accent-orange-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-gray-500">
                      {item.itemCode || "—"}
                    </td>
                    <td className="px-2 py-1.5 text-gray-300 max-w-[160px] truncate">
                      {item.customerDescription ||
                        item.costCodeDescription ||
                        "—"}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      {item.sectionNumber != null ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSectionFilter(item.sectionNumber);
                          }}
                          className={cls(
                            "text-[10px] font-mono px-1.5 py-0.5 rounded transition-colors max-w-[120px] truncate block text-left",
                            sectionFilter === item.sectionNumber
                              ? "bg-indigo-500/30 text-indigo-300 border border-indigo-500/50"
                              : "text-indigo-400 bg-indigo-900/30 hover:bg-indigo-900/50",
                          )}
                          title={sectionLabel(item.sectionNumber)}
                        >
                          {sectionLabel(item.sectionNumber)}
                        </button>
                      ) : (
                        <span className="text-gray-600 text-[10px]">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      {item.bidArea ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-gray-700 text-gray-300">
                          {item.bidArea}
                        </span>
                      ) : (
                        <span className="text-gray-600 italic text-[10px]">
                          none
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      {mapped ? (
                        <span className="text-purple-400 text-[10px] font-medium">
                          {tab === "M"
                            ? `PO#${mapped.poNumber} ${mapped.title || ""}`
                            : `WO#${mapped.woNumber} ${mapped.title || ""}`}
                        </span>
                      ) : (
                        <span className="text-gray-600 text-[10px]">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">
          {selectedLocalIds.length} of {visibleItems.length} selected
          {(bidAreaFilter !== "all" || sectionFilter !== "all") && (
            <button
              onClick={() => {
                setBidAreaFilter("all");
                setSectionFilter("all");
              }}
              className="ml-2 text-orange-400 hover:text-orange-300 underline text-[10px]"
            >
              clear filters
            </button>
          )}
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedLocalIds([])}
            disabled={!selectedLocalIds.length}
            className="px-3 py-1.5 text-xs text-gray-400 bg-gray-700 border border-gray-600 rounded hover:bg-gray-600 disabled:opacity-40"
          >
            Clear
          </button>
          <button
            onClick={applyMassEdit}
            disabled={!canApply}
            className="px-4 py-1.5 text-xs font-semibold text-white bg-orange-500 rounded hover:bg-orange-600 disabled:opacity-40"
          >
            Apply to {selectedLocalIds.length || 0} item
            {selectedLocalIds.length !== 1 ? "s" : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Section Card ───────────────────────────────────────────────────────────
function SectionCard({
  section,
  sectionIdx,
  allItems,
  markupPct,
  isReadOnly,
  isExpanded,
  onDuplicateItem,
  onDuplicateSection,
  isFirst = false,
  isLast = false,
  onToggle,
  onUpdateSection,
  onRemoveSection,
  onMoveSection,
  onUpdateItem,
  onToggleBreakdown,
  onAddCustomItem,
  budgetItemMap = {},
  onUpdateCustomItem,
  onRemoveCustomItem,
  onMoveItem,
  onMoveCustom,
  onMoveSectionItem,
  onRemoveItemFromSection,
  bdTotals,
  databaseItems,
  purchaseOrders,
  workOrders,
  knownBidAreas,
  contractLabourRates = [],
  projectId,
  token,
  onScopeRefsImported,
}) {
  const [activeTab, setActiveTab] = useState("items");
  const [showPicker, setShowPicker] = useState(false);
  const [itemsExpanded, setItemsExpanded] = useState(true);

  const sectionItems = allItems.filter(
    (i) => i.referenceNumber === section.number,
  );
  const bd = bdTotals[section.number] || {
    matTotal: 0,
    labTotal: 0,
    matItems: [],
    labItems: [],
  };
  const customItems = section.customItems || [];

  const hasBdM =
    (bd.matItems?.length || 0) +
      customItems.filter((c) => c.category === "M").length >
    0;
  const hasBdL =
    (bd.labItems?.length || 0) +
      customItems.filter((c) => c.category === "L").length >
    0;

  const matPrice = hasBdM ? bd.matTotal : section.materialPrice || 0;
  const labPrice = hasBdL ? bd.labTotal : section.laborPrice || 0;
  const sectionTotal = matPrice + labPrice;

  const estCost = sectionItems.reduce((s, i) => s + (i.amount || 0), 0);
  const suggested = estCost * (1 + (markupPct || 0) / 100);
  const margin =
    sectionTotal !== 0
      ? ((sectionTotal - estCost) / Math.abs(sectionTotal)) * 100
      : 0;
  const bdCount =
    (bd.matItems?.length || 0) +
    (bd.labItems?.length || 0) +
    customItems.length;
  const sectionBidAreas = useMemo(
    () => [...new Set(sectionItems.map((i) => i.bidArea).filter(Boolean))],
    [sectionItems],
  );

  return (
    <>
      {!isReadOnly && showPicker && (
        <COItemSearchHelper
          open={showPicker}
          onClose={() => setShowPicker(false)}
          databaseItems={databaseItems}
          purchaseOrders={purchaseOrders}
          workOrders={workOrders}
          sectionNumber={section.number}
          knownBidAreas={knownBidAreas}
          projectId={projectId}
          token={token}
          onAdd={(newItems, scopeRefs) => {
            onUpdateItem(
              "_batch_add",
              newItems.map(({ _sheetName, ...i }) => ({
                ...i,
                referenceNumber: section.number,
              })),
            );
            if (scopeRefs?.length && onScopeRefsImported)
              onScopeRefsImported(scopeRefs);
            setShowPicker(false);
          }}
        />
      )}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {/* Header */}
        <div
          className="flex items-center gap-3 px-4 py-3.5 cursor-pointer select-none hover:bg-gray-700/30 transition-colors"
          onClick={onToggle}
        >
          <div className="w-9 h-9 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-base shrink-0">
            {section.number}
          </div>
          <div className="flex-1 min-w-0">
            {isReadOnly ? (
              <p className="text-sm font-semibold text-white truncate">
                {section.title || `Section ${section.number}`}
              </p>
            ) : (
              <input
                value={section.title || ""}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  onUpdateSection(sectionIdx, "title", e.target.value);
                }}
                placeholder="Section title…"
                className="w-full bg-transparent border-b border-transparent hover:border-gray-500 focus:border-orange-500 focus:outline-none text-sm font-semibold text-white"
              />
            )}
            <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-500 flex-wrap">
              <span>
                {sectionItems.length} cost item
                {sectionItems.length !== 1 ? "s" : ""}
              </span>
              {sectionBidAreas.length > 0 && (
                <span className="text-gray-600">
                  {sectionBidAreas.join(", ")}
                </span>
              )}
              {estCost > 0 && (
                <span>
                  Est cost:{" "}
                  <span className="text-gray-400">{fmtC(estCost)}</span> ·
                  Suggested:{" "}
                  <span className="text-orange-300">{fmtC(suggested)}</span>
                </span>
              )}
              {sectionTotal > 0 && estCost > 0 && (
                <span
                  className={margin >= 0 ? "text-green-400" : "text-red-400"}
                >
                  Margin: {margin.toFixed(1)}%
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right">
              <p className="text-[10px] text-gray-500">Customer Price</p>
              <p
                className={`text-lg font-bold tabular-nums ${sectionTotal < 0 ? "text-red-400" : "text-green-300"}`}
              >
                {fmtC(sectionTotal)}
              </p>
            </div>
            {!isReadOnly && (
              <div
                className="flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col gap-0.5">
                  <button
                    disabled={isFirst}
                    onClick={() => onMoveSection && onMoveSection(-1)}
                    className={cls(
                      "w-6 h-5 flex items-center justify-center rounded text-[10px] leading-none transition-colors",
                      isFirst
                        ? "text-gray-700 cursor-not-allowed"
                        : "text-gray-500 hover:text-orange-300 hover:bg-gray-700",
                    )}
                    title="Move section up"
                  >
                    ▲
                  </button>
                  <button
                    disabled={isLast}
                    onClick={() => onMoveSection && onMoveSection(1)}
                    className={cls(
                      "w-6 h-5 flex items-center justify-center rounded text-[10px] leading-none transition-colors",
                      isLast
                        ? "text-gray-700 cursor-not-allowed"
                        : "text-gray-500 hover:text-orange-300 hover:bg-gray-700",
                    )}
                    title="Move section down"
                  >
                    ▼
                  </button>
                </div>
                <button
                  onClick={() =>
                    onDuplicateSection && onDuplicateSection(section.number)
                  }
                  title="Duplicate this section and its items"
                  className="p-1.5 text-gray-500 hover:text-blue-300 hover:bg-blue-900/20 rounded transition-colors text-sm leading-none"
                >
                  ⧉
                </button>

                <button
                  onClick={() => onRemoveSection(sectionIdx)}
                  className="p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-900/20 rounded transition-colors text-lg leading-none"
                >
                  ×
                </button>
              </div>
            )}
            <span className="text-gray-500 text-xs">
              {isExpanded ? "▲" : "▼"}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-gray-700">
            {/* Tabs */}
            <div className="flex border-b border-gray-700 px-4">
              {[
                ["items", `Cost Items (${sectionItems.length})`],
                ["breakdown", `Customer Breakdown (${bdCount})`],
              ].map(([tab, label]) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={cls(
                    "px-4 py-2.5 text-xs font-medium border-b-2 transition-colors -mb-px",
                    activeTab === tab
                      ? "border-orange-500 text-orange-400"
                      : "border-transparent text-gray-400 hover:text-gray-200",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Cost Items tab */}
            {activeTab === "items" && (
              <div className="p-4">
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {hasBdM || hasBdL
                    ? [
                        ["M", "Material (breakdown)", "blue", bd.matTotal],
                        ["L", "Labour (breakdown)", "orange", bd.labTotal],
                      ].map(([cat, label, color, total]) => {
                        const catItems = sectionItems.filter(
                          (i) => i.category === cat,
                        );
                        const catCost = catItems.reduce(
                          (s, i) => s + (i.amount || 0),
                          0,
                        );
                        return (
                          <div
                            key={cat}
                            className="bg-gray-700/30 rounded-lg p-3 border border-dashed border-gray-600"
                          >
                            <p className="text-[11px] text-gray-500 mb-1">
                              {label}
                            </p>
                            <p
                              className={`text-xl font-bold text-${color}-300 tabular-nums`}
                            >
                              {fmtC(total || 0)}
                            </p>
                            <p className="text-[10px] text-gray-500 mt-1">
                              Computed from Breakdown tab
                            </p>
                            {catItems.length > 0 && (
                              <p className="text-[10px] text-gray-600 mt-0.5">
                                Internal cost:{" "}
                                <span className="text-gray-400">
                                  {fmtC(catCost)}
                                </span>
                                {total > 0 && catCost > 0 && (
                                  <span
                                    className={cls(
                                      "ml-1",
                                      total >= catCost
                                        ? "text-green-500"
                                        : "text-red-400",
                                    )}
                                  >
                                    ({total >= catCost ? "+" : ""}
                                    {(
                                      ((total - catCost) / Math.abs(catCost)) *
                                      100
                                    ).toFixed(1)}
                                    %)
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                        );
                      })
                    : [
                        [
                          "materialPrice",
                          "Material Price (manual)",
                          "blue",
                          "M",
                        ],
                        ["laborPrice", "Labour Price (manual)", "orange", "L"],
                      ].map(([field, label, color, cat]) => {
                        const catItems = sectionItems.filter(
                          (i) => i.category === cat,
                        );
                        const catCost = catItems.reduce(
                          (s, i) => s + (i.amount || 0),
                          0,
                        );
                        return (
                          <div
                            key={field}
                            className="bg-gray-700/50 rounded-lg p-3 border border-gray-600"
                          >
                            <p className="text-[11px] text-gray-500 mb-1.5">
                              {label}
                            </p>
                            {isReadOnly ? (
                              <p
                                className={`text-xl font-bold text-${color}-300 tabular-nums`}
                              >
                                {fmtC(section[field] || 0)}
                              </p>
                            ) : (
                              <input
                                type="number"
                                step="0.01"
                                value={section[field] || ""}
                                onChange={(e) =>
                                  onUpdateSection(
                                    sectionIdx,
                                    field,
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className={`w-full text-right text-xl font-bold bg-gray-800 border border-gray-600 rounded px-3 py-2 text-${color}-300 focus:outline-none focus:border-orange-500`}
                              />
                            )}
                            {catItems.length > 0 && (
                              <p className="text-[10px] text-gray-600 mt-1.5">
                                Internal cost:{" "}
                                <span className="text-gray-400">
                                  {fmtC(catCost)}
                                </span>
                                {section[field] > 0 && catCost > 0 && (
                                  <span
                                    className={cls(
                                      "ml-1",
                                      section[field] >= catCost
                                        ? "text-green-500"
                                        : "text-red-400",
                                    )}
                                  >
                                    ({section[field] >= catCost ? "+" : ""}
                                    {(
                                      ((section[field] - catCost) /
                                        Math.abs(catCost)) *
                                      100
                                    ).toFixed(1)}
                                    %)
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                        );
                      })}
                </div>

                {/* Add Items + collapse toggle */}
                <div className="flex items-center justify-between mb-3">
                  {!isReadOnly && (
                    <button
                      onClick={() => setShowPicker(true)}
                      className="px-3 py-1.5 text-xs text-blue-300 bg-blue-900/30 border border-blue-700/40 rounded-lg hover:bg-blue-900/50 font-medium"
                    >
                      + Add Items
                    </button>
                  )}
                  {sectionItems.length > 0 && (
                    <button
                      onClick={() => setItemsExpanded((p) => !p)}
                      className="ml-auto px-3 py-1.5 text-xs text-white bg-orange-700 border border-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      {itemsExpanded
                        ? `▲ Collapse items`
                        : `▼ Show ${sectionItems.length} item${sectionItems.length !== 1 ? "s" : ""}`}
                    </button>
                  )}
                </div>

                {sectionItems.length === 0 ? (
                  <div className="text-center py-6 border border-dashed border-gray-700 rounded-lg">
                    <p className="text-gray-600 text-xs mb-2">
                      No cost items in this section yet.
                    </p>
                    {!isReadOnly && (
                      <button
                        onClick={() => setShowPicker(true)}
                        className="text-xs text-blue-400 hover:text-blue-300 underline underline-offset-2"
                      >
                        + Add Items
                      </button>
                    )}
                  </div>
                ) : !itemsExpanded ? (
                  /* Collapsed summary */
                  <div className="rounded-lg border border-gray-700/50 px-4 py-3 flex flex-wrap gap-4 text-xs text-gray-500">
                    {(() => {
                      const mCost = sectionItems
                        .filter((i) => i.category === "M")
                        .reduce((s, i) => s + (i.amount || 0), 0);
                      const lCost = sectionItems
                        .filter((i) => i.category === "L")
                        .reduce((s, i) => s + (i.amount || 0), 0);
                      const mCount = sectionItems.filter(
                        (i) => i.category === "M",
                      ).length;
                      const lCount = sectionItems.filter(
                        (i) => i.category === "L",
                      ).length;
                      const bdCount = sectionItems.filter(
                        (i) => i.includeInBreakdown,
                      ).length;
                      const unmapped = sectionItems.filter((i) =>
                        i.category === "M" ? !i.mappedPO3 : !i.mappedWO3,
                      ).length;
                      return (
                        <>
                          {mCount > 0 && (
                            <span>
                              <span className="text-blue-400 font-semibold">
                                {mCount}M
                              </span>
                              {" · "}
                              <span className="text-blue-300/70">
                                {fmtC(mCost)}
                              </span>
                            </span>
                          )}
                          {lCount > 0 && (
                            <span>
                              <span className="text-orange-400 font-semibold">
                                {lCount}L
                              </span>
                              {" · "}
                              <span className="text-orange-300/70">
                                {fmtC(lCost)}
                              </span>
                            </span>
                          )}
                          <span className="text-green-300 font-semibold">
                            Total: {fmtC(mCost + lCost)}
                          </span>
                          {bdCount > 0 && (
                            <span className="text-green-500">
                              {bdCount} in breakdown
                            </span>
                          )}
                          {unmapped > 0 && (
                            <span className="text-yellow-400">
                              ⚠ {unmapped} unmapped
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-700/50">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-900/60">
                        <tr>
                          {!isReadOnly && (
                            <th className="px-1 py-1.5 w-8 text-center text-gray-600 font-medium">
                              ↕
                            </th>
                          )}
                          {[
                            "Cat",
                            "Code",
                            "Description",
                            "Bid Area",
                            "Size",
                            "Unit Cost",
                            "UOM",
                            "Qty Δ",
                            "Total Cost",
                            "Map to PO/WO",
                            "Brkdwn",
                            "",
                          ].map((h) => (
                            <th
                              key={h}
                              className="px-2 py-1.5 text-left text-gray-500 font-medium whitespace-nowrap"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sectionItems.map((item, itemViewPos) => {
                          const isSFLF =
                            item.category === "M" && isCovUom(item.uom);

                          const dupSizeWarn =
                            isSFLF &&
                            sectionItems.some((other) => {
                              if (other._localId === item._localId)
                                return false;
                              return (
                                (other.bidArea || "").trim().toLowerCase() ===
                                  (item.bidArea || "").trim().toLowerCase() &&
                                other.itemCode === item.itemCode &&
                                (item.size || "").trim() !== "" &&
                                (other.size || "").trim().toLowerCase() ===
                                  (item.size || "").trim().toLowerCase()
                              );
                            });
                          const unitCostVal =
                            item.category === "L"
                              ? item.unitRate || 0
                              : item.unitCost || 0;
                          const mapOpts = getMappingOptions(
                            item,
                            purchaseOrders,
                            workOrders,
                          );
                          const isFirstItem = itemViewPos === 0;
                          const isLastItem =
                            itemViewPos === sectionItems.length - 1;

                          return (
                            <tr
                              key={item._localId || item._id}
                              className="border-t border-gray-700/30 hover:bg-gray-700/20"
                            >
                              {!isReadOnly && (
                                <td className="px-1 py-1.5 w-8">
                                  <div className="flex flex-col items-center gap-0.5">
                                    <button
                                      disabled={isFirstItem}
                                      onClick={() =>
                                        onMoveSectionItem &&
                                        onMoveSectionItem(
                                          section.number,
                                          item._localId,
                                          -1,
                                        )
                                      }
                                      className={cls(
                                        "w-5 h-4 flex items-center justify-center rounded text-[10px] leading-none transition-colors",
                                        isFirstItem
                                          ? "text-gray-700 cursor-not-allowed"
                                          : "text-gray-500 hover:text-orange-300 hover:bg-gray-700",
                                      )}
                                      title="Move up"
                                    >
                                      ▲
                                    </button>
                                    <button
                                      disabled={isLastItem}
                                      onClick={() =>
                                        onMoveSectionItem &&
                                        onMoveSectionItem(
                                          section.number,
                                          item._localId,
                                          1,
                                        )
                                      }
                                      className={cls(
                                        "w-5 h-4 flex items-center justify-center rounded text-[10px] leading-none transition-colors",
                                        isLastItem
                                          ? "text-gray-700 cursor-not-allowed"
                                          : "text-gray-500 hover:text-orange-300 hover:bg-gray-700",
                                      )}
                                      title="Move down"
                                    >
                                      ▼
                                    </button>
                                  </div>
                                </td>
                              )}
                              <td className="px-2 py-1.5">
                                <span
                                  className={cls(
                                    "inline-block px-1 py-0.5 text-[10px] font-bold rounded",
                                    item.category === "M"
                                      ? "bg-blue-900/60 text-blue-300"
                                      : "bg-orange-900/60 text-orange-300",
                                  )}
                                >
                                  {item.category}
                                </span>
                              </td>
                              <td className="px-2 py-1.5 font-mono text-[10px] text-gray-400">
                                {item.itemCode || "—"}
                              </td>
                              <td className="px-2 py-1.5 text-gray-200 max-w-[240px]">
                                <span className="block text-[11px] leading-tight break-words">
                                  {item.customerDescription ||
                                    item.costCodeDescription ||
                                    "—"}
                                </span>
                              </td>
                              <td className="px-2 py-1.5 max-w-[240px]">
                                {isReadOnly ? (
                                  <span className="text-gray-400 text-[11px]">
                                    {item.bidArea || "—"}
                                  </span>
                                ) : (
                                  <BidAreaCombobox
                                    value={item.bidArea || ""}
                                    onChange={(v) => {
                                      onUpdateItem(item._localId, "bidArea", v);
                                      if (v) {
                                        const mapped = autoMapForBidArea(
                                          v,
                                          item.category,
                                          purchaseOrders,
                                          workOrders,
                                          budgetItemMap,
                                          item.itemCode,
                                        );
                                        if (
                                          item.category === "M" &&
                                          mapped.mappedPO3 &&
                                          !item.mappedPO3
                                        )
                                          onUpdateItem(
                                            item._localId,
                                            "mappedPO3",
                                            mapped.mappedPO3,
                                            "mappedWO3",
                                          );
                                        else if (
                                          item.category === "L" &&
                                          mapped.mappedWO3 &&
                                          !item.mappedWO3
                                        )
                                          onUpdateItem(
                                            item._localId,
                                            "mappedWO3",
                                            mapped.mappedWO3,
                                            "mappedPO3",
                                          );
                                      }
                                    }}
                                    knownAreas={knownBidAreas}
                                    className="w-48"
                                  />
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-center">
                                {isSFLF ? (
                                  <div className="flex flex-col items-center gap-0.5">
                                    {isReadOnly ? (
                                      <span className="text-gray-400 text-[11px]">
                                        {item.size || "—"}
                                      </span>
                                    ) : (
                                      <input
                                        value={item.size || ""}
                                        onChange={(e) =>
                                          onUpdateItem(
                                            item._localId,
                                            "size",
                                            e.target.value,
                                          )
                                        }
                                        placeholder={
                                          item.uom?.toLowerCase().includes("sf")
                                            ? "4x8"
                                            : "12'"
                                        }
                                        className={cls(
                                          "w-24 text-center bg-gray-700/60 border rounded px-1 py-0.5 text-blue-200 text-[11px] focus:outline-none",
                                          dupSizeWarn
                                            ? "border-yellow-500 focus:border-yellow-400"
                                            : "border-blue-700/40 focus:border-blue-400",
                                        )}
                                      />
                                    )}
                                    {dupSizeWarn && (
                                      <span
                                        title="Same size already exists for this item in this bid area"
                                        className="text-[9px] text-yellow-400 leading-none cursor-default select-none"
                                      >
                                        ⚠ dup
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-600">—</span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-right font-mono">
                                {isSFLF && item.coveragePerUnit ? (
                                  <>
                                    <span className="text-gray-200 font-semibold">
                                      {fmtC(
                                        unitCostVal *
                                          (item.coveragePerUnit || 1),
                                      )}
                                    </span>
                                    <span className="block text-[9px] text-gray-500">
                                      {fmtC(unitCostVal)}/
                                      {(item.uom || "").toUpperCase()}
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-gray-200 font-semibold">
                                    {fmtC(unitCostVal)}
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-center">
                                {isSFLF ? (
                                  <span className="text-gray-200 font-semibold text-[11px]">
                                    PC
                                  </span>
                                ) : (
                                  <span className="text-[11px] text-gray-400">
                                    {item.uom || "—"}
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-center">
                                {isReadOnly ? (
                                  <span
                                    className={cls(
                                      "font-mono font-semibold tabular-nums",
                                      (item.deltaQty || 0) < 0
                                        ? "text-red-400"
                                        : "text-white",
                                    )}
                                  >
                                    {(item.deltaQty || 0) > 0 ? "+" : ""}
                                    {item.deltaQty ?? 0}
                                    {isSFLF && (
                                      <span className="block text-[9px] text-gray-500 font-normal">
                                        ={" "}
                                        {fmtQty(
                                          (item.deltaQty || 0) *
                                            (item.coveragePerUnit || 1),
                                        )}{" "}
                                        {(item.uom || "").toUpperCase()}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      value={item.deltaQty ?? ""}
                                      onChange={(e) =>
                                        onUpdateItem(
                                          item._localId,
                                          "deltaQty",
                                          parseFloat(e.target.value) || 0,
                                        )
                                      }
                                      className={cls(
                                        "w-20 text-center bg-gray-700/60 border border-gray-600 rounded px-1 py-0.5 font-mono",
                                        "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
                                        "focus:border-orange-500 focus:outline-none",
                                        (item.deltaQty || 0) < 0
                                          ? "text-red-400"
                                          : "text-white",
                                      )}
                                    />
                                    {isSFLF && item.coveragePerUnit && (
                                      <span className="text-[9px] text-gray-500">
                                        ={" "}
                                        {fmtQty(
                                          (parseFloat(item.deltaQty) || 0) *
                                            (item.coveragePerUnit || 1),
                                        )}{" "}
                                        {(item.uom || "").toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td
                                className={cls(
                                  "px-2 py-1.5 text-right font-mono font-semibold",
                                  (item.amount || 0) < 0
                                    ? "text-red-400"
                                    : "text-green-300",
                                )}
                              >
                                {fmtC(item.amount || 0)}
                              </td>
                              <td className="px-2 py-1.5 min-w-[160px]">
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-[9px] text-gray-600">
                                    {mapOpts.label}
                                  </span>
                                  <select
                                    value={mapOpts.value}
                                    disabled={isReadOnly}
                                    onChange={(e) => {
                                      if (!isReadOnly) {
                                        const val = e.target.value || null;
                                        if (mapOpts.field === "mappedPO3")
                                          onUpdateItem(
                                            item._localId,
                                            "mappedPO3",
                                            val,
                                            "mappedWO3",
                                          );
                                        else
                                          onUpdateItem(
                                            item._localId,
                                            "mappedWO3",
                                            val,
                                            "mappedPO3",
                                          );
                                      }
                                    }}
                                    className={cls(
                                      "w-full text-xs bg-gray-700 border rounded px-1.5 py-1 text-gray-200 focus:border-orange-500 focus:outline-none",
                                      mapOpts.value
                                        ? "border-purple-600/50"
                                        : "border-gray-600",
                                      isReadOnly
                                        ? "opacity-80 cursor-default"
                                        : "",
                                    )}
                                  >
                                    <option value="">— None —</option>
                                    {mapOpts.options.map((o) => (
                                      <option key={o.value} value={o.value}>
                                        {o.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </td>
                              <td className="px-2 py-1.5 text-center">
                                {isReadOnly ? (
                                  <span
                                    className={cls(
                                      "text-[10px]",
                                      item.includeInBreakdown
                                        ? "text-green-400"
                                        : "text-gray-600",
                                    )}
                                  >
                                    {item.includeInBreakdown ? "✓" : "—"}
                                  </span>
                                ) : (
                                  <button
                                    onClick={() =>
                                      onToggleBreakdown(
                                        item._localId,
                                        !item.includeInBreakdown,
                                      )
                                    }
                                    className={cls(
                                      "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent",
                                      "transition-colors duration-200 ease-in-out focus:outline-none",
                                      item.includeInBreakdown
                                        ? "bg-green-500"
                                        : "bg-gray-600 hover:bg-gray-500",
                                    )}
                                    role="switch"
                                    aria-checked={item.includeInBreakdown}
                                  >
                                    <span
                                      className={cls(
                                        "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-md",
                                        "transform transition-transform duration-200 ease-in-out",
                                        item.includeInBreakdown
                                          ? "translate-x-4"
                                          : "translate-x-0",
                                      )}
                                    />
                                  </button>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-center">
                                {!isReadOnly && (
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() =>
                                        onDuplicateItem(item._localId)
                                      }
                                      title="Duplicate row"
                                      className="px-1.5 py-0.5 text-[11px] text-gray-400 bg-gray-700 hover:bg-gray-600 hover:text-gray-200 rounded border border-gray-600 leading-none transition-colors"
                                    >
                                      ⧉
                                    </button>
                                    <button
                                      onClick={() =>
                                        onRemoveItemFromSection(item._localId)
                                      }
                                      title="Remove from section"
                                      className="text-gray-600 hover:text-red-400 text-base leading-none transition-colors"
                                    >
                                      ×
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="border-t border-gray-700 bg-gray-900/40">
                        {(() => {
                          const mCost = sectionItems
                            .filter((i) => i.category === "M")
                            .reduce((s, i) => s + (i.amount || 0), 0);
                          const lCost = sectionItems
                            .filter((i) => i.category === "L")
                            .reduce((s, i) => s + (i.amount || 0), 0);
                          const totalInternal = mCost + lCost;
                          const colSpan = isReadOnly ? 8 : 9;
                          return (
                            <>
                              {mCost !== 0 && (
                                <tr>
                                  <td
                                    colSpan={colSpan}
                                    className="px-2 py-1 text-right text-[10px] text-blue-400/70"
                                  >
                                    Material cost:
                                  </td>
                                  <td className="px-2 py-1 text-right font-mono text-blue-400/80 text-[10px]">
                                    {fmtC(mCost)}
                                  </td>
                                  <td colSpan={3} />
                                </tr>
                              )}
                              {lCost !== 0 && (
                                <tr>
                                  <td
                                    colSpan={colSpan}
                                    className="px-2 py-1 text-right text-[10px] text-orange-400/70"
                                  >
                                    Labour cost:
                                  </td>
                                  <td className="px-2 py-1 text-right font-mono text-orange-400/80 text-[10px]">
                                    {fmtC(lCost)}
                                  </td>
                                  <td colSpan={3} />
                                </tr>
                              )}
                              <tr className="border-t border-gray-700/50">
                                <td
                                  colSpan={colSpan}
                                  className="px-2 py-1.5 text-right text-[11px] text-gray-500"
                                >
                                  Total internal cost:
                                </td>
                                <td className="px-2 py-1.5 text-right font-bold font-mono text-green-300">
                                  {fmtC(totalInternal)}
                                </td>
                                <td colSpan={3} />
                              </tr>
                            </>
                          );
                        })()}
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Breakdown tab */}
            {activeTab === "breakdown" && (
              <div className="p-4 space-y-4">
                <p className="text-xs text-gray-500">
                  Toggle{" "}
                  <span className="text-green-400 font-medium">
                    In Breakdown
                  </span>{" "}
                  on cost items above to include them here with{" "}
                  <span className="text-orange-300">{markupPct}% markup</span>{" "}
                  auto-applied. Add Custom rows for any unlisted line items.
                </p>
                {["M", "L"].map((cat) => (
                  <BreakdownTable
                    key={cat}
                    category={cat}
                    bdItems={
                      cat === "M" ? bd.matItems || [] : bd.labItems || []
                    }
                    customItems={customItems}
                    total={cat === "M" ? bd.matTotal : bd.labTotal}
                    sectionIdx={sectionIdx}
                    isReadOnly={isReadOnly}
                    markupPct={markupPct}
                    onUpdateItem={onUpdateItem}
                    onUpdateCustomItem={onUpdateCustomItem}
                    onRemoveCustomItem={onRemoveCustomItem}
                    onAddCustomItem={onAddCustomItem}
                    onMoveItem={onMoveItem}
                    onMoveCustom={onMoveCustom}
                    contractLabourRates={contractLabourRates}
                  />
                ))}
                <div className="flex justify-end border-t border-gray-700 pt-3 gap-6">
                  {estCost > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-gray-500 mb-0.5">
                        Internal Cost
                      </p>
                      <p className="text-sm text-gray-400 tabular-nums">
                        {fmtC(estCost)}
                      </p>
                    </div>
                  )}
                  <div className="text-right">
                    <p className="text-sm text-gray-500 mb-0.5">
                      Section Customer Total
                    </p>
                    <p
                      className={`text-xl font-bold tabular-nums ${sectionTotal < 0 ? "text-red-400" : "text-green-300"}`}
                    >
                      {fmtC(sectionTotal)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ── Main Export ────────────────────────────────────────────────────────────
export default function COPricingSectionsV3({
  sections,
  budgetItemMap = {},
  setSections,
  items,
  setItems,
  markupPct = 30,
  overheadPct = 0,
  isReadOnly = false,
  databaseItems = [],
  purchaseOrders = [],
  workOrders = [],
  knownBidAreas = [],
  contractLabourRates = [],
  projectId,
  token,
  onScopeRefsImported,
  unions = [],
}) {
  const [expanded, setExpanded] = useState(new Set());
  const [showMassEdit, setShowMassEdit] = useState(false);

  useEffect(() => {
    if (!setSections) return;
    setSections((prev) =>
      prev.map((s) => {
        const bdItems = (items || []).filter(
          (i) => i.referenceNumber === s.number && i.includeInBreakdown,
        );
        const hasBdM =
          bdItems.filter((i) => i.category === "M").length +
            (s.customItems || []).filter((c) => c.category === "M").length >
          0;
        const hasBdL =
          bdItems.filter((i) => i.category === "L").length +
            (s.customItems || []).filter((c) => c.category === "L").length >
          0;
        if ((hasBdM && s.materialPrice) || (hasBdL && s.laborPrice)) {
          return {
            ...s,
            materialPrice: hasBdM ? 0 : s.materialPrice,
            laborPrice: hasBdL ? 0 : s.laborPrice,
          };
        }
        return s;
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── resolve the correct sell price for a breakdown labour item ─────────────
  const resolveLabourBreakdownPrice = useCallback(
    (item) => {
      const match = contractLabourRates.find((r) => {
        const rateCode = (
          r.itemCode ||
          r.labourItem?.itemCode ||
          r.labourItem?.code ||
          ""
        )
          .trim()
          .toLowerCase();
        const rateTitle = (r.labourItem?.title || r.title || "")
          .trim()
          .toLowerCase();
        const itemCode = (item.itemCode || "").trim().toLowerCase();
        const itemDesc = (
          item.customerDescription ||
          item.costCodeDescription ||
          ""
        )
          .trim()
          .toLowerCase();
        if (rateCode && itemCode && rateCode === itemCode) return true;
        if (
          rateTitle &&
          itemDesc &&
          (itemDesc.includes(rateTitle) || rateTitle.includes(itemDesc))
        )
          return true;
        return false;
      });
      if (match) return parseFloat(match.unitRate) || 0;
      // fallback: unitRate + markup
      return Number(
        ((parseFloat(item.unitRate) || 0) * (1 + markupPct / 100)).toFixed(2),
      );
    },
    [contractLabourRates, markupPct],
  );

  const bdTotals = useMemo(() => {
    const t = {};
    sections.forEach((s) => {
      const bdItems = (items || []).filter(
        (i) => i.referenceNumber === s.number && i.includeInBreakdown,
      );
      let matTotal = 0,
        labTotal = 0;
      const matItems = [],
        labItems = [];
      bdItems.forEach((item) => {
        const qty = item.breakdownQty ?? Math.abs(item.deltaQty || 0);
        const price =
          item.breakdownUnitPrice !== undefined &&
          item.breakdownUnitPrice !== null
            ? item.breakdownUnitPrice
            : item.category === "L"
              ? resolveLabourBreakdownPrice(item)
              : calcAutoPrice(item, markupPct);
        const total = qty * price;
        const bi = {
          ...item,
          breakdownQty: +qty.toFixed(2),
          breakdownUnitPrice: +price.toFixed(2),
        };
        if (item.category === "M") {
          matTotal += total;
          matItems.push(bi);
        } else {
          labTotal += total;
          labItems.push(bi);
        }
      });
      (s.customItems || []).forEach((ci) => {
        const ciTotal =
          (parseFloat(ci.qty) || 0) * (parseFloat(ci.unitPrice) || 0);
        if (ci.category === "M") matTotal += ciTotal;
        else labTotal += ciTotal;
      });
      t[s.number] = {
        matTotal: +matTotal.toFixed(2),
        labTotal: +labTotal.toFixed(2),
        matItems,
        labItems,
      };
    });
    return t;
  }, [items, sections, markupPct]);

  const { sub, overhead, total } = useMemo(() => {
    let s = 0;
    sections.forEach((sec) => {
      const bd = bdTotals[sec.number] || {};
      const hasBdM =
        (bd.matItems?.length || 0) +
          (sec.customItems || []).filter((c) => c.category === "M").length >
        0;
      const hasBdL =
        (bd.labItems?.length || 0) +
          (sec.customItems || []).filter((c) => c.category === "L").length >
        0;
      s += hasBdM ? bd.matTotal || 0 : sec.materialPrice || 0;
      s += hasBdL ? bd.labTotal || 0 : sec.laborPrice || 0;
    });
    const oh = s * (overheadPct / 100);
    return { sub: s, overhead: oh, total: s + oh };
  }, [sections, bdTotals, overheadPct]);

  const toggleExpand = (n) =>
    setExpanded((p) => {
      const s = new Set(p);
      s.has(n) ? s.delete(n) : s.add(n);
      return s;
    });

  const addSection = () => {
    const n = sections.length + 1;
    setSections((p) => [
      ...p,
      {
        number: n,
        title: "",
        materialPrice: 0,
        laborPrice: 0,
        customItems: [],
      },
    ]);
    setExpanded((p) => new Set([...p, n]));
  };

  const updateSection = (sectionNumber, field, value) =>
    setSections((p) =>
      p.map((s) => (s.number === sectionNumber ? { ...s, [field]: value } : s)),
    );

  const removeSection = (sectionNumber) => {
    const remaining = sections
      .filter((s) => s.number !== sectionNumber)
      .sort((a, b) => a.number - b.number);
    const remap = {};
    remaining.forEach((s, idx) => {
      remap[s.number] = idx + 1;
    });
    if (setItems) {
      setItems((p) =>
        p.map((i) => {
          if (i.referenceNumber === sectionNumber)
            return { ...i, referenceNumber: null, includeInBreakdown: false };
          if (
            i.referenceNumber != null &&
            remap[i.referenceNumber] !== undefined
          )
            return { ...i, referenceNumber: remap[i.referenceNumber] };
          return i;
        }),
      );
    }
    setSections(remaining.map((s, idx) => ({ ...s, number: idx + 1 })));
    setExpanded((p) => {
      const next = new Set();
      p.forEach((n) => {
        if (n !== sectionNumber && remap[n] !== undefined) next.add(remap[n]);
      });
      return next;
    });
  };

  // ── Section reorder ──────────────────────────────────────────────────────
  const moveSection = (sectionNumber, direction) => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.number === sectionNumber);
      const targetIdx = idx + direction;
      if (idx < 0 || targetIdx < 0 || targetIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
      const remap = {};
      next.forEach((s, i) => {
        remap[s.number] = i + 1;
      });
      if (setItems) {
        setItems((pi) =>
          pi.map((item) =>
            item.referenceNumber != null &&
            remap[item.referenceNumber] !== undefined
              ? { ...item, referenceNumber: remap[item.referenceNumber] }
              : item,
          ),
        );
      }
      return next.map((s, i) => ({ ...s, number: i + 1 }));
    });
  };

  // ── Section duplicate — clones section + all its cost items + custom items ─
  const duplicateSection = (sectionNumber) => {
    if (!setSections) return;

    const newNum =
      sections.length > 0 ? Math.max(...sections.map((s) => s.number)) + 1 : 1;

    const original = sections.find((s) => s.number === sectionNumber);
    if (!original) return;

    // Clone the section itself, with fresh customItem ids
    const clonedCustomItems = (original.customItems || []).map((ci) => ({
      ...ci,
      _id: `ci-dup-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    }));

    const clonedSection = {
      ...original,
      number: newNum,
      title: original.title ? `${original.title} (Copy)` : "",
      customItems: clonedCustomItems,
    };

    setSections((prev) => [...prev, clonedSection]);

    // Clone every cost item that points at this section, re-pointed at newNum
    if (setItems) {
      setItems((prev) => {
        const toClone = prev.filter((i) => i.referenceNumber === sectionNumber);
        const cloned = toClone.map((item) => ({
          ...item,
          _localId: `dup-sec-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          referenceNumber: newNum,
          // reset mapping confirmation so duplicated items get re-mapped/reviewed
          mappingConfirmed: false,
        }));
        return [...prev, ...cloned];
      });
    }

    // Auto-expand the new section so the user sees the result immediately
    setExpanded((p) => new Set([...p, newNum]));
  };

  // ── Breakdown item reorder (Customer Breakdown tab — toggled items) ───────
  const moveBdItem = (sectionNumber, localId, direction) => {
    if (!setItems) return;
    setItems((prev) => {
      const sectionBd = prev
        .map((item, idx) => ({ item, idx }))
        .filter(
          ({ item }) =>
            item.referenceNumber === sectionNumber && item.includeInBreakdown,
        );
      const pos = sectionBd.findIndex(({ item }) => item._localId === localId);
      const targetPos = pos + direction;
      if (pos < 0 || targetPos < 0 || targetPos >= sectionBd.length)
        return prev;
      const fromIdx = sectionBd[pos].idx;
      const toIdx = sectionBd[targetPos].idx;
      const next = [...prev];
      [next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
      return next;
    });
  };

  // ── Custom item reorder (Customer Breakdown tab — custom rows) ────────────
  const moveCustomItem = (sectionNumber, ciId, direction) => {
    if (!setSections) return;
    setSections((prev) =>
      prev.map((s) => {
        if (s.number !== sectionNumber) return s;
        const customs = [...(s.customItems || [])];
        const idx = customs.findIndex((c) => c._id === ciId);
        const targetIdx = idx + direction;
        if (idx < 0 || targetIdx < 0 || targetIdx >= customs.length) return s;
        [customs[idx], customs[targetIdx]] = [customs[targetIdx], customs[idx]];
        return { ...s, customItems: customs };
      }),
    );
  };

  // ── Cost item reorder within a section (Cost Items tab) ──────────────────
  const moveSectionItem = (sectionNumber, localId, direction) => {
    if (!setItems) return;
    setItems((prev) => {
      const sectionEntries = prev
        .map((item, idx) => ({ item, idx }))
        .filter(({ item }) => item.referenceNumber === sectionNumber);
      const pos = sectionEntries.findIndex(
        ({ item }) => item._localId === localId,
      );
      const targetPos = pos + direction;
      if (pos < 0 || targetPos < 0 || targetPos >= sectionEntries.length)
        return prev;
      const fromIdx = sectionEntries[pos].idx;
      const toIdx = sectionEntries[targetPos].idx;
      const next = [...prev];
      [next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
      return next;
    });
  };

  const unionOptions = useMemo(() => {
    const company = (unions || []).map((u) => u.title).filter(Boolean);
    const fromWOs = (workOrders || [])
      .map((w) => w.union)
      .filter((u) => u && u !== "non-union" && u !== "union");
    const all = [...new Set([...company, ...fromWOs])];
    return [
      ...all.map((title) => ({ value: title, label: title })),
      { value: "non-union", label: "Non-Union" },
    ];
  }, [unions, workOrders]);

  const handleItemUpdate = (
    localId,
    fieldOrItems,
    value,
    clearField = null,
  ) => {
    if (!setItems) return;
    if (localId === "_batch_add" && Array.isArray(fieldOrItems)) {
      const hasSheets = fieldOrItems.some((i) => i._sheetName);
      if (hasSheets && setSections) {
        setSections((prev) => {
          const sheetNames = [
            ...new Set(fieldOrItems.map((i) => i._sheetName).filter(Boolean)),
          ];
          let nextNum =
            prev.length > 0 ? Math.max(...prev.map((s) => s.number)) + 1 : 1;
          const sectionMap = {};
          const newSections = [];
          sheetNames.forEach((name) => {
            const existing = prev.find((s) => s.title === name);
            if (existing) {
              sectionMap[name] = existing.number;
            } else {
              sectionMap[name] = nextNum;
              newSections.push({
                number: nextNum,
                title: name,
                materialPrice: 0,
                laborPrice: 0,
                customItems: [],
              });
              nextNum++;
            }
          });
          setItems((p) => [
            ...p,
            ...fieldOrItems.map((item) => ({
              ...item,
              _localId: `batch-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              referenceNumber: sectionMap[item._sheetName] ?? null,
            })),
          ]);
          setExpanded((p) => {
            const s = new Set(p);
            Object.values(sectionMap).forEach((n) => s.add(n));
            return s;
          });
          return [...prev, ...newSections];
        });
        return;
      }
      setItems((p) => [...p, ...fieldOrItems]);
      return;
    }
    setItems((p) =>
      p.map((i) => {
        if (i._localId !== localId) return i;
        const u = { ...i, [fieldOrItems]: value };
        if (clearField) u[clearField] = null;
        if (fieldOrItems === "mappedPO3" || fieldOrItems === "mappedWO3")
          u.mappingConfirmed = !!value;
        if (fieldOrItems === "size" && i.category === "M" && isCovUom(i.uom)) {
          const computed = computeCoverageFromSize(value, i.uom);
          u.coveragePerUnit =
            computed !== null
              ? computed
              : value?.trim()
                ? parseFloat(i.coveragePerUnit) || 1
                : 1;
        }
        if (
          fieldOrItems === "breakdownQty" ||
          fieldOrItems === "breakdownUnitPrice"
        )
          u._breakdownPriceOverridden = true;
        if (
          [
            "deltaQty",
            "size",
            "coveragePerUnit",
            "unitCost",
            "unitRate",
          ].includes(fieldOrItems)
        ) {
          const qty = Math.abs(
            parseFloat(fieldOrItems === "deltaQty" ? value : u.deltaQty) || 0,
          );
          const cov =
            u.category === "M" && isCovUom(u.uom)
              ? parseFloat(u.coveragePerUnit) || 1
              : 1;
          const base = u.category === "L" ? u.unitRate || 0 : u.unitCost || 0;
          const raw = qty * cov * base;
          u.quantity = qty;
          u.amount =
            (parseFloat(fieldOrItems === "deltaQty" ? value : u.deltaQty) ||
              0) < 0
              ? -raw
              : raw;
        }
        return u;
      }),
    );
  };

  const toggleBreakdown = (localId, val) => {
    if (!setItems) return;
    const markup = markupPct;
    setItems((p) =>
      p.map((i) => {
        if (i._localId !== localId) return i;
        const u = { ...i, includeInBreakdown: val };
        if (val) {
          // ── Qty ──────────────────────────────────────────────────────────
          if (u.breakdownQty == null) {
            u.breakdownQty = Math.abs(u.deltaQty || 0) || 1;
          }

          // ── Description ──────────────────────────────────────────────────
          if (!u.breakdownDescription) {
            u.breakdownDescription =
              u.customerDescription || u.costCodeDescription || "";
          }

          // ── UOM ──────────────────────────────────────────────────────────
          if (!u.breakdownUnit) {
            u.breakdownUnit =
              u.category === "L" ? (u.uom || "HR").toUpperCase() : "EA";
          }

          // ── Unit Price ────────────────────────────────────────────────────
          if (!u._breakdownPriceOverridden || !(u.breakdownUnitPrice > 0)) {
            let computed = 0;

            if (u.category === "L") {
              // Try to match a contract labour rate by itemCode first, then
              // by title/description substring match as fallback
              const contractMatch = contractLabourRates.find((r) => {
                const rateCode = (
                  r.itemCode ||
                  r.labourItem?.itemCode ||
                  r.labourItem?.code ||
                  ""
                )
                  .trim()
                  .toLowerCase();
                const rateTitle = (r.labourItem?.title || r.title || "")
                  .trim()
                  .toLowerCase();

                const itemCode = (u.itemCode || "").trim().toLowerCase();
                const itemDesc = (
                  u.customerDescription ||
                  u.costCodeDescription ||
                  ""
                )
                  .trim()
                  .toLowerCase();

                // Exact itemCode match is strongest signal
                if (rateCode && itemCode && rateCode === itemCode) return true;

                // Title/description substring match as fallback
                if (
                  rateTitle &&
                  itemDesc &&
                  (itemDesc.includes(rateTitle) || rateTitle.includes(itemDesc))
                )
                  return true;

                return false;
              });

              if (contractMatch) {
                // Contract rate is already the sell price — no markup applied
                computed = parseFloat(contractMatch.unitRate) || 0;
                // Also sync the UOM to match the contract rate
                u.breakdownUnit = (
                  contractMatch.uom ||
                  u.breakdownUnit ||
                  "HR"
                ).toUpperCase();
              } else {
                // No contract match — apply markup to internal unitRate
                const base = parseFloat(u.unitRate) || 0;
                const markupMultiplier = 1 + (markup || 0) / 100;
                computed = Number((base * markupMultiplier).toFixed(2));
              }
            } else {
              // Material: unitCost * coveragePerUnit (for SF/LF) * markup
              const base = parseFloat(u.unitCost) || 0;
              const cov = isCovUom(u.uom)
                ? parseFloat(u.coveragePerUnit) || 1
                : 1;
              const markupMultiplier = 1 + (markup || 0) / 100;
              computed = Number((base * cov * markupMultiplier).toFixed(2));
            }

            u.breakdownUnitPrice = computed;
            u._breakdownPriceOverridden = false;
          }
        }
        return u;
      }),
    );
  };

  const removeItemFromSection = (localId) => {
    if (!setItems) return;
    setItems((p) => p.filter((i) => i._localId !== localId));
  };

  const duplicateItem = (localId) => {
    if (!setItems) return;
    setItems((p) => {
      const idx = p.findIndex((i) => i._localId === localId);
      if (idx === -1) return p;
      const original = p[idx];
      const clone = {
        ...original,
        _localId: `dup-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        includeInBreakdown: false,
        breakdownQty: undefined,
        breakdownUnitPrice: undefined,
        breakdownDescription: undefined,
        breakdownUnit: undefined,
        _breakdownPriceOverridden: false,
      };
      const next = [...p];
      next.splice(idx + 1, 0, clone); // inserts immediately after original
      return next;
    });
  };

  const { resolveName } = useCostCodeMap();

  const addCustomItem = (sectionNumber, cat) => {
    if (!setSections) return;
    setSections((p) =>
      p.map((s) => {
        if (s.number !== sectionNumber) return s;
        return {
          ...s,
          customItems: [
            ...(s.customItems || []),
            {
              _id: `ci-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              category: cat,
              description: "",
              qty: 1,
              unit: "EA",
              unitPrice: 0,
              total: 0,
            },
          ],
        };
      }),
    );
  };

  const updateCustomItem = (sectionNumber, ciId, field, value) => {
    if (!setSections) return;
    setSections((p) =>
      p.map((s) => {
        if (s.number !== sectionNumber) return s;
        return {
          ...s,
          customItems: (s.customItems || []).map((ci) => {
            if (ci._id !== ciId) return ci;
            const u = { ...ci, [field]: value };
            u.total = parseFloat(
              (
                (parseFloat(u.qty) || 0) * (parseFloat(u.unitPrice) || 0)
              ).toFixed(2),
            );
            return u;
          }),
        };
      }),
    );
  };

  const removeCustomItem = (sectionNumber, ciId) => {
    if (!setSections) return;
    setSections((p) =>
      p.map((s) =>
        s.number !== sectionNumber
          ? s
          : {
              ...s,
              customItems: (s.customItems || []).filter((c) => c._id !== ciId),
            },
      ),
    );
  };

  return (
    <div className="space-y-4">
      {sections.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Subtotal", value: sub, defaultColor: "text-gray-200" },
            {
              label: `Overhead (${overheadPct}%)`,
              value: overhead,
              defaultColor: "text-gray-300",
            },
            {
              label: "Customer Total",
              value: total,
              defaultColor: "text-orange-400",
            },
          ].map(({ label, value, defaultColor }) => (
            <div
              key={label}
              className="bg-gray-900/60 rounded-lg border border-gray-700 px-4 py-3"
            >
              <p className="text-[15px] text-gray-500 uppercase tracking-wide mb-1">
                {label}
              </p>
              <p
                className={`text-xl font-bold tabular-nums ${value < 0 ? "text-red-400" : defaultColor}`}
              >
                {fmtC(value)}
              </p>
            </div>
          ))}
        </div>
      )}

      {!isReadOnly && showMassEdit && (
        <MassEditPanel
          items={items || []}
          sections={sections}
          purchaseOrders={purchaseOrders}
          workOrders={workOrders}
          knownBidAreas={knownBidAreas}
          unionOptions={unionOptions}
          budgetItemMap={budgetItemMap}
          onUpdateItem={handleItemUpdate}
          onClose={() => setShowMassEdit(false)}
          projectId={projectId}
          token={token}
          resolveName={resolveName}
        />
      )}

      {!isReadOnly && (items || []).some((i) => i.referenceNumber != null) && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowMassEdit((p) => !p)}
            className={cls(
              "px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors",
              showMassEdit
                ? "bg-orange-500/20 border-orange-500/50 text-orange-300"
                : "bg-gray-700 border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-500",
            )}
          >
            {showMassEdit ? "✕ Close Mass Edit" : "⊞ Mass Edit Bid Areas"}
          </button>
        </div>
      )}

      {sections.map((section, secArrayIdx) => (
        <SectionCard
          key={section.number}
          section={section}
          sectionIdx={section.number}
          onDuplicateItem={duplicateItem}
          onDuplicateSection={duplicateSection}
          allItems={items || []}
          markupPct={markupPct}
          budgetItemMap={budgetItemMap}
          isReadOnly={isReadOnly}
          isFirst={secArrayIdx === 0}
          isLast={secArrayIdx === sections.length - 1}
          isExpanded={expanded.has(section.number)}
          onToggle={() => toggleExpand(section.number)}
          onUpdateSection={updateSection}
          onRemoveSection={removeSection}
          onMoveSection={(dir) => moveSection(section.number, dir)}
          onUpdateItem={handleItemUpdate}
          onToggleBreakdown={toggleBreakdown}
          onRemoveItemFromSection={removeItemFromSection}
          onAddCustomItem={addCustomItem}
          onUpdateCustomItem={updateCustomItem}
          onRemoveCustomItem={removeCustomItem}
          onMoveItem={(secNum, localId, dir) =>
            moveBdItem(secNum, localId, dir)
          }
          onMoveCustom={(secNum, ciId, dir) =>
            moveCustomItem(secNum, ciId, dir)
          }
          onMoveSectionItem={(secNum, localId, dir) =>
            moveSectionItem(secNum, localId, dir)
          }
          bdTotals={bdTotals}
          databaseItems={databaseItems}
          purchaseOrders={purchaseOrders}
          workOrders={workOrders}
          knownBidAreas={knownBidAreas}
          contractLabourRates={contractLabourRates}
          projectId={projectId}
          token={token}
          onScopeRefsImported={onScopeRefsImported}
        />
      ))}

      {!isReadOnly && (
        <button
          onClick={addSection}
          className="w-full py-3 text-sm text-orange-400 border-2 border-dashed border-orange-500/30 rounded-xl hover:border-orange-500/60 hover:bg-orange-900/10 transition-colors font-medium"
        >
          + Add Pricing Section
        </button>
      )}

      {sections.length === 0 && isReadOnly && (
        <p className="text-center text-sm text-gray-500 py-6">
          No pricing sections
        </p>
      )}
    </div>
  );
}
